import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import GavelIcon from '@mui/icons-material/Gavel'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SubscriptionAgreementDocument from '../../../legal/SubscriptionAgreementDocument'
import { SoftChip } from '../../kit/Chips'
import KeyValueList from '../../kit/KeyValueList'
import SectionCard from '../../kit/SectionCard'
import { formatDate, formatDateTime } from '../../kit/helpers'
import { normaliseCadence } from '../tenantHelpers'
import {
  ACCEPTANCE_REASONS,
  ACCEPTANCE_SOURCES,
  AGREEMENT_REASONS,
  AGREEMENT_STATUS,
  CONFIRMATIONS,
} from './constants'

const versionLabel = (version) => (version ? `v${version}` : '')

const acceptanceState = (acceptance) => {
  if (acceptance.inForce) {
    return { label: 'In force', color: '#00964C', hint: 'The acceptance the contract currently rests on.' }
  }
  if (acceptance.applied) {
    return { label: 'Applied', color: '#64748B', hint: 'Applied to the contract at the time; since superseded by a later acceptance.' }
  }
  return {
    label: 'Not applied',
    color: '#F28B1F',
    hint: 'The evidence was recorded but this acceptance never took effect on the contract (for example a second submission arriving just after the first).',
  }
}

// One immutable acceptance record: who accepted which version, when, from
// where, and the three authority confirmations. Collapsed to a header line
// unless it is the record in force.
const AcceptanceRecord = ({ acceptance, open, onToggle, onRead, onPlanSummary }) => {
  const state = acceptanceState(acceptance)
  return (
    <Box sx={{ border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        component="button"
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        sx={{
          width: '100%',
          textAlign: 'left',
          border: 0,
          bgcolor: open ? '#F8FAFC' : 'transparent',
          cursor: 'pointer',
          fontFamily: 'inherit',
          px: 1.5,
          py: 1,
          '&:hover': { bgcolor: '#F8FAFC' },
        }}
      >
        <Box minWidth={0} flex={1}>
          <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" fontWeight={700} color="text.primary">
              {versionLabel(acceptance.agreementVersion) || 'Unknown version'}
            </Typography>
            <Tooltip describeChild title={state.hint}>
              <span>
                <SoftChip color={state.color} label={state.label} sx={{ height: 20, fontSize: 11 }} />
              </span>
            </Tooltip>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatDateTime(acceptance.acceptedAt)}
            {acceptance.acceptedByName ? ` · ${acceptance.acceptedByName}` : ''}
          </Typography>
        </Box>
        <ExpandMoreIcon
          fontSize="small"
          sx={{ color: 'text.secondary', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}
        />
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Stack gap={1.5} sx={{ px: 1.5, py: 1.5, borderTop: '1px solid #EEF2F7' }}>
          <KeyValueList
            showEmpty
            rows={[
              { label: 'Name', value: acceptance.acceptedByName },
              { label: 'Email', value: acceptance.acceptedByEmail, fullWidth: true },
              { label: 'Stated role', value: acceptance.acceptedByRole },
              { label: 'Accepted at', value: formatDateTime(acceptance.acceptedAt) },
              { label: 'Source', value: ACCEPTANCE_SOURCES[acceptance.source] ?? acceptance.source },
              { label: 'Reason', value: ACCEPTANCE_REASONS[acceptance.reason] ?? acceptance.reason },
              { label: 'IP address', value: acceptance.ipAddress },
              { label: 'Effective from', value: formatDateTime(acceptance.effectiveFrom) },
              {
                label: 'Package accepted',
                value: [acceptance.package?.packageName, normaliseCadence(acceptance.package?.billingCadence)]
                  .filter(Boolean)
                  .join(' · '),
              },
              { label: 'Agreement dated', value: formatDate(acceptance.agreementEffectiveDate) },
              { label: 'User agent', value: acceptance.userAgent, fullWidth: true },
              {
                label: 'Authority confirmations',
                fullWidth: true,
                render: (
                  <Stack gap={0.25}>
                    {CONFIRMATIONS.map((confirmation) => {
                      const ticked = acceptance.confirmations?.[confirmation.key] === true
                      return (
                        <Stack key={confirmation.key} direction="row" gap={0.75} alignItems="center">
                          {ticked ? (
                            <CheckIcon sx={{ fontSize: 16, color: '#00964C' }} />
                          ) : (
                            <CloseIcon sx={{ fontSize: 16, color: '#B42318' }} />
                          )}
                          <Typography variant="body2" fontWeight={500}>
                            {confirmation.label}
                            {ticked ? '' : ' - not confirmed'}
                          </Typography>
                        </Stack>
                      )
                    })}
                  </Stack>
                ),
              },
              {
                label: 'Agreement text SHA-256',
                value: acceptance.agreementSha256,
                fullWidth: true,
                render: acceptance.agreementSha256 ? (
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {acceptance.agreementSha256}
                  </Typography>
                ) : null,
              },
            ]}
          />
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              onClick={onRead}
              disabled={!acceptance.agreementVersion}
            >
              Read {versionLabel(acceptance.agreementVersion) || 'agreement'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={onPlanSummary}
              disabled={!acceptance.planSummaryText}
            >
              View Plan Summary
            </Button>
          </Stack>
        </Stack>
      </Collapse>
    </Box>
  )
}

// Agreement status for the tenant plus the acceptance evidence: every
// immutable acceptance record, the agreement text for its version and the
// Plan Summary that was stored with it. Read-only - acceptance only ever
// happens in the customer's own app.
const AgreementCard = ({ agreement }) => {
  const acceptances = agreement?.acceptances ?? []
  const summary = agreement?.summary ?? {}
  const [expanded, setExpanded] = useState(() => {
    const first = acceptances.find((acceptance) => acceptance.inForce) ?? acceptances[0]
    return first ? String(first._id) : null
  })
  const [readVersion, setReadVersion] = useState(null)
  const [planSummary, setPlanSummary] = useState(null)

  const status = AGREEMENT_STATUS[agreement?.status] ?? AGREEMENT_STATUS.notRequired

  return (
    <SectionCard
      title="Agreement"
      icon={<GavelIcon />}
      subtitle="AmbuNet Business Subscription Agreement"
    >
      <Stack gap={2}>
        <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
          <SoftChip color={status.color} label={status.label} />
          {summary.acceptedVersion && (
            <Typography variant="body2" color="text.secondary">
              {versionLabel(summary.acceptedVersion)} accepted {formatDate(summary.acceptedAt)}
            </Typography>
          )}
        </Stack>
        {agreement?.status === 'pending' && (
          <Alert severity="warning">
            Pending - {AGREEMENT_REASONS[agreement.reason] ?? 'acceptance is outstanding'}. The
            account owner sees a reminder in the app; their access is never restricted by it.
          </Alert>
        )}
        {agreement?.status === 'notRequired' && (
          <Alert severity="info">
            No agreement is required for this tenant (demo tenant, or no plan yet).
          </Alert>
        )}
        <KeyValueList
          showEmpty
          rows={[
            { label: 'Current version', value: versionLabel(agreement?.currentVersion) },
            { label: 'Required version', value: versionLabel(agreement?.requiredVersion) },
            { label: 'Accepted version', value: versionLabel(summary.acceptedVersion) },
            {
              label: 'Accepted by',
              value: [summary.acceptedByName, summary.acceptedByEmail].filter(Boolean).join(' · '),
              fullWidth: true,
            },
            { label: 'Effective from', value: formatDateTime(summary.effectiveFrom) },
            { label: 'Monitoring from', value: formatDateTime(summary.monitoringFrom) },
          ]}
        />
        {agreement?.acceptedUrl && (
          <Link
            href={agreement.acceptedUrl}
            target="_blank"
            rel="noreferrer"
            variant="body2"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, alignSelf: 'flex-start' }}
          >
            Read the accepted agreement ({versionLabel(summary.acceptedVersion)})
            <OpenInNewIcon sx={{ fontSize: 15 }} />
          </Link>
        )}

        <Box>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              mb: 1,
            }}
          >
            Acceptance evidence ({acceptances.length})
          </Typography>
          {acceptances.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No acceptance has been recorded for this tenant.
            </Typography>
          ) : (
            <Stack gap={1}>
              {acceptances.map((acceptance) => {
                const id = String(acceptance._id)
                return (
                  <AcceptanceRecord
                    key={id}
                    acceptance={acceptance}
                    open={expanded === id}
                    onToggle={() => setExpanded(expanded === id ? null : id)}
                    onRead={() => setReadVersion(acceptance.agreementVersion)}
                    onPlanSummary={() => setPlanSummary(acceptance)}
                  />
                )
              })}
            </Stack>
          )}
        </Box>
      </Stack>

      <Dialog open={!!readVersion} onClose={() => setReadVersion(null)} maxWidth="md" fullWidth>
        <DialogTitle>Business Subscription Agreement {versionLabel(readVersion)}</DialogTitle>
        <DialogContent dividers>
          {readVersion && <SubscriptionAgreementDocument version={readVersion} compact />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReadVersion(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!planSummary} onClose={() => setPlanSummary(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Plan Summary accepted {formatDate(planSummary?.acceptedAt)}
        </DialogTitle>
        <DialogContent dividers>
          <Stack gap={1.5}>
            <Typography variant="body2" color="text.secondary">
              The exact Plan Summary stored with this acceptance
              {planSummary?.acceptedByName ? ` by ${planSummary.acceptedByName}` : ''}. It is part
              of the evidence and cannot be changed.
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                border: '1px solid #E2E8F0',
                borderRadius: 2,
                bgcolor: '#F8FAFC',
                fontFamily: 'monospace',
                fontSize: 13,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {planSummary?.planSummaryText}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanSummary(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  )
}

export default AgreementCard
